import { createAddonCart } from '@theme/diceprime-cart-core';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const node = document.getElementById('DiceprimeAddonsConfig');
const config = node ? JSON.parse(node.textContent) : null;
const choices = new Map();
let pending = 0;
let message = '';
let unsafe = false;
const components = () => [...document.querySelectorAll('cart-items-component[data-section-id]')];
const sectionIds = () => [...new Set(components().map(element => element.dataset.sectionId))].slice(0, 5);
const service = config ? createAddonCart({
  config, fetcher: window.fetch.bind(window), root: window.Shopify?.routes?.root || '/', sectionIds,
  sectionUrl: () => window.location.pathname,
  onState: value => { pending = value; sync(); }
}) : null;

function sync() {
  document.documentElement.classList.toggle('diceprime-cart-busy', pending > 0 || unsafe);
  document.querySelectorAll('[data-diceprime-addon]').forEach(input => {
    const choice = choices.get(input.dataset.diceprimeAddon);
    if (choice) input.checked = choice.selected;
    input.closest('.diceprime-addons__option')?.setAttribute('aria-busy', String(!!choice));
  });
  document.querySelectorAll('[data-diceprime-status]').forEach(element => {
    const text = pending ? config.updating : message;
    if (element.textContent !== text) element.textContent = text;
  });
  document.querySelectorAll('[data-diceprime-retry]').forEach(button => { button.hidden = !message || pending > 0; });
  const id = config?.items.find(item => item.key === 'shipping')?.id;
  if (id) document.querySelectorAll(`cart-quantity-selector-component[data-variant-id="${id}"]`).forEach(control => {
    control.classList.add('diceprime-single-quantity');
    const input = control.querySelector('input');
    if (input) { input.readOnly = true; input.max = '1'; }
    control.querySelectorAll('button').forEach(button => { button.disabled = true; });
  });
}

async function render(cart) {
  const focusKey = document.activeElement?.dataset?.diceprimeAddon;
  const scroll = ['.cart-drawer__content', '.cart-drawer__items'].map(selector => ({ selector, top: document.querySelector(selector)?.scrollTop || 0 }));
  const targets = components();
  for (const component of targets) {
    const id = component.dataset.sectionId;
    if (!cart.sections?.[id]) throw new Error(config.error);
    sectionRenderer.abortRender(id);
    await morphSection(id, cart.sections[id], { mode: component.hasAttribute('data-drawer') ? 'hydration' : 'full', injectStylesheet: true });
  }
  const deferred = CartLinesUpdateEvent.createPromise();
  const source = components()[0] || document;
  source.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
    sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'diceprime-addons', didError: false
  } });
  sync();
  if (focusKey) document.querySelector(`[data-diceprime-addon="${focusKey}"]`)?.focus({ preventScroll: true });
  scroll.forEach(({ selector, top }) => { const element = document.querySelector(selector); if (element) element.scrollTop = top; });
  document.querySelector('cart-items-component')?.dispatchEvent(new Event('cart-section:restored', { bubbles: true }));
}

async function recover() {
  service.invalidate();
  try {
    await render(await service.withSections(await service.read()));
    unsafe = false;
  } catch { unsafe = true; }
  sync();
}

export function cartRequest(url, init) {
  if (!service) return fetch(url, init);
  return service.request(url, init).catch(async error => {
    message = config.error;
    unsafe = true;
    await service.enqueue(recover);
    throw error;
  });
}

if (service) {
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-diceprime-addon]');
    if (!input) return;
    event.stopImmediatePropagation();
    const key = input.dataset.diceprimeAddon;
    const choice = { selected: input.checked };
    choices.set(key, choice);
    service.enqueue(async () => {
      if (choices.get(key) !== choice) return;
      message = '';
      try {
        const cart = await service.toggle(key, choice.selected);
        if (choices.get(key) === choice) choices.delete(key);
        await render(cart);
        unsafe = false;
      } catch {
        if (choices.get(key) === choice) choices.delete(key);
        message = config.error;
        unsafe = true;
        await recover();
      }
    }).catch(() => { message = config.error; unsafe = true; sync(); });
  }, true);

  const blocked = '[name="checkout"],a[href*="/checkout"],.cart__additional-checkout-buttons,cart-quantity-selector-component,.cart-items__remove,product-form-component [type="submit"],sticky-add-to-cart [type="submit"]';
  document.addEventListener('click', event => {
    if (event.target.closest('[data-diceprime-retry]')) {
      service.enqueue(async () => { message = ''; await recover(); if (unsafe) message = config.error; sync(); });
    } else if ((pending || unsafe) && event.target.closest(blocked)) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if ((pending || unsafe) && (event.target.matches('.cart-form') || event.target.closest('product-form-component') || event.submitter?.name === 'checkout')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('quantity-selector:update', event => {
    if (!pending && !unsafe) return;
    const control = event.target.closest('cart-quantity-selector-component');
    if (!control) return;
    event.stopImmediatePropagation();
    const input = control.querySelector('input');
    if (input) input.value = input.defaultValue;
  }, true);
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    event.promise?.then(() => requestAnimationFrame(sync)).catch(() => {});
  });
  const check = () => {
    if (pending || !components().length) return;
    service.enqueue(async () => {
      try {
        const result = await service.refresh();
        if (result.changed) await render(result.cart);
        unsafe = false;
      } catch { message = config.error; unsafe = true; await recover(); }
    }).catch(() => { message = config.error; unsafe = true; sync(); });
  };
  document.querySelector('#cart-drawer')?.addEventListener('theme-drawer:open', check);
  window.addEventListener('pageshow', () => {
    if (document.querySelector('#cart-drawer[open]') || document.querySelector('main[data-template="cart"]')) check();
  });
  window.addEventListener('focus', () => service.invalidate());
  sync();
}
